import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Camera, Trash2, Upload, Eye, Check, AlertCircle } from "lucide-react";
import { useOutlet } from "@/contexts/OutletContext";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

type SavingSection = "storefront" | "profile" | "contact" | "social" | null;

// Extracted Social Helpers matching Onboarding
const sanitizeHandle = (v: string) => v.replace(/[^a-zA-Z0-9_.]/g, "").slice(0, 60);

const extractHandle = (url: string | undefined, baseUrl: string): string => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/^\//, "").replace(/\/$/, "");
    return path || "";
  } catch {
    return url.replace(/^\//, "");
  }
};

const SOCIAL_CONFIGS = {
  instagram_url: { baseUrl: "https://instagram.com/", display: "instagram.com/", label: "Instagram", placeholder: "yourhandle" },
  facebook_url: { baseUrl: "https://facebook.com/", display: "facebook.com/", label: "Facebook", placeholder: "yourhandle" },
  twitter_url: { baseUrl: "https://x.com/", display: "x.com/", label: "Twitter/X", placeholder: "yourhandle" },
} as const;

interface BusinessDetails {
  business_name: string;
  description: string;
  address: string;
  phone_number: string;
  business_email: string;
  instagram_url: string;
  facebook_url: string;
  twitter_url: string;
  avatar_url: string;
  slug: string;
}

const StorefrontBusinessDetails = () => {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const { activeOutlet, loading: outletLoading, refreshOutlets } = useOutlet();
  const [savingSection, setSavingSection] = useState<SavingSection>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [initialSlug, setInitialSlug] = useState("");
  const [formData, setFormData] = useState<BusinessDetails>({
    business_name: "",
    description: "",
    address: "",
    phone_number: "",
    business_email: "",
    instagram_url: "",
    facebook_url: "",
    twitter_url: "",
    avatar_url: "",
    slug: "",
  });
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Programme refund policy (business-level). Governs how a prepaid/upfront
  // term is refunded when a customer cancels remaining sessions.
  const [refundPolicy, setRefundPolicy] = useState<"pro_rata" | "credit_only" | "no_refund">("pro_rata");
  const [savingRefund, setSavingRefund] = useState(false);
  // Phase C3. Auto-offer owed make-up seats to parents over their channel.
  const [makeUpAutoOffer, setMakeUpAutoOffer] = useState(false);
  const [savingMakeUp, setSavingMakeUp] = useState(false);
  // Most upcoming bookings one customer can hold (null = no limit). Hidden
  // until it loads, so the card never shows a value it can't save.
  const [bookingLimitLoaded, setBookingLimitLoaded] = useState(false);
  const [bookingLimitOn, setBookingLimitOn] = useState(true);
  const [bookingLimit, setBookingLimit] = useState("3");
  const [savingBookingLimit, setSavingBookingLimit] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("businesses")
        .select("refund_policy, make_up_auto_offer")
        .eq("business_id", businessId)
        .maybeSingle();
      const row = data as { refund_policy?: string; make_up_auto_offer?: boolean } | null;
      const p = row?.refund_policy;
      if (!cancelled && (p === "pro_rata" || p === "credit_only" || p === "no_refund")) {
        setRefundPolicy(p);
      }
      if (!cancelled) setMakeUpAutoOffer(!!row?.make_up_auto_offer);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any)
        .from("businesses")
        .select("max_upcoming_bookings_per_contact")
        .eq("business_id", businessId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const limit = data.max_upcoming_bookings_per_contact as number | null;
      setBookingLimitOn(limit !== null);
      setBookingLimit(String(limit ?? 3));
      setBookingLimitLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const handleSaveBookingLimit = async () => {
    if (!businessId) return;
    const limit = parseInt(bookingLimit, 10);
    if (bookingLimitOn && !(limit >= 1 && limit <= 50)) {
      toast.error("Choose a number from 1 to 50");
      return;
    }
    setSavingBookingLimit(true);
    const { error } = await (supabase as any)
      .from("businesses")
      .update({ max_upcoming_bookings_per_contact: bookingLimitOn ? limit : null })
      .eq("business_id", businessId);
    setSavingBookingLimit(false);
    if (error) {
      toast.error("Couldn't save the booking limit");
    } else {
      toast.success(bookingLimitOn ? `Limit set to ${limit} upcoming bookings` : "Booking limit turned off");
    }
  };

  const handleToggleMakeUpAutoOffer = async (next: boolean) => {
    if (!businessId) return;
    setMakeUpAutoOffer(next); // optimistic
    setSavingMakeUp(true);
    const { error } = await supabase
      .from("businesses")
      .update({ make_up_auto_offer: next })
      .eq("business_id", businessId);
    setSavingMakeUp(false);
    if (error) {
      setMakeUpAutoOffer(!next); // revert
      toast.error("Couldn't update make-up auto-offer");
    } else {
      toast.success(next ? "Make-up auto-offer turned on" : "Make-up auto-offer turned off");
    }
  };

  const handleSaveRefundPolicy = async () => {
    if (!businessId) return;
    setSavingRefund(true);
    const { error } = await supabase
      .from("businesses")
      .update({ refund_policy: refundPolicy })
      .eq("business_id", businessId);
    setSavingRefund(false);
    if (error) {
      toast.error("Failed to save refund policy");
    } else {
      toast.success("Refund policy saved");
    }
  };

  // Populate form from activeOutlet data (outlet-specific fields)
  useEffect(() => {
    if (outletLoading || !activeOutlet) return;

    const currentSlug = activeOutlet.slug || "";

    setFormData({
      business_name: activeOutlet.outlet_name || "",
      description: activeOutlet.description || "",
      address: activeOutlet.address || "",
      phone_number: activeOutlet.phone_number || "",
      business_email: activeOutlet.business_email || "",
      instagram_url: activeOutlet.instagram_url || "",
      facebook_url: activeOutlet.facebook_url || "",
      twitter_url: activeOutlet.twitter_url || "",
      avatar_url: activeOutlet.avatar_url || "",
      slug: currentSlug,
    });
    setInitialSlug(activeOutlet.slug || "");
    setSlugStatus('idle');
  }, [activeOutlet, outletLoading]);

  // Slug uniqueness check
  useEffect(() => {
    const checkSlug = async () => {
      const slug = formData.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

      if (!slug) {
        setSlugStatus('idle');
        return;
      }

      if (slug === initialSlug) {
        setSlugStatus('idle');
        return;
      }

      setSlugStatus('checking');

      // Check against outlets slug column, excluding the current outlet
      const { data: outletMatch } = await supabase
        .from("outlets")
        .select("outlet_id")
        .eq("slug", slug)
        .neq("outlet_id", activeOutlet?.outlet_id || "")
        .maybeSingle();

      // Also check against businesses slug column for legacy compatibility
      const { data: bizMatch } = await supabase
        .from("businesses")
        .select("business_id")
        .eq("slug", slug)
        .neq("business_id", businessId!)
        .maybeSingle();

      if (outletMatch || bizMatch) {
        setSlugStatus('taken');
      } else {
        setSlugStatus('available');
      }
    };

    const timer = setTimeout(checkSlug, 500);
    return () => clearTimeout(timer);
  }, [formData.slug, initialSlug, activeOutlet?.outlet_id, businessId]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeOutlet || !businessId) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const filePath = `${businessId}/profile/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("business-assets")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast.error("Failed to upload image");
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from("business-assets")
      .getPublicUrl(filePath);

    setFormData((prev) => ({ ...prev, avatar_url: urlData.publicUrl }));
    toast.success("Image uploaded");

    // Auto-save the avatar to outlet row
    await supabase.from("outlets").update({ avatar_url: urlData.publicUrl }).eq("outlet_id", activeOutlet.outlet_id);
    await refreshOutlets();
    setUploading(false);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, avatar_url: "" }));
  };

  // Section-specific save handlers

  const handleSaveStorefront = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOutlet) return;
    setSavingSection("storefront");

    const cleanSlug = formData.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

    const { error } = await supabase
      .from("outlets")
      .update({ slug: cleanSlug || null })
      .eq("outlet_id", activeOutlet.outlet_id);

    // Also keep the businesses.slug in sync for the main outlet (first outlet)
    if (!error) {
      const { data: firstOutlet } = await supabase
        .from("outlets")
        .select("outlet_id")
        .eq("business_id", businessId!)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (firstOutlet?.outlet_id === activeOutlet.outlet_id) {
        await supabase
          .from("businesses")
          .update({ slug: cleanSlug || null })
          .eq("business_id", businessId!);
      }
    }

    if (error) {
      toast.error("Failed to save storefront settings");
    } else {
      toast.success("Storefront settings saved");
      setInitialSlug(cleanSlug);
      setSlugStatus('idle');
      await refreshOutlets();
    }
    setSavingSection(null);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOutlet) return;
    setSavingSection("profile");

    const { error } = await supabase
      .from("outlets")
      .update({
        outlet_name: formData.business_name,
        description: formData.description,
        avatar_url: formData.avatar_url,
      })
      .eq("outlet_id", activeOutlet.outlet_id);

    if (error) {
      toast.error("Failed to save profile");
    } else {
      toast.success("Profile saved");
      await refreshOutlets();
    }
    setSavingSection(null);
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOutlet) return;

    // Validate phone
    if (formData.phone_number && formData.phone_number.trim() !== '') {
      if (!isValidPhoneNumber(formData.phone_number)) {
        setPhoneError("Enter a valid phone number");
        toast.error("Enter a valid phone number");
        return;
      }
    }

    setSavingSection("contact");

    const { error } = await supabase
      .from("outlets")
      .update({
        address: formData.address,
        phone_number: formData.phone_number,
        business_email: formData.business_email,
      })
      .eq("outlet_id", activeOutlet.outlet_id);

    if (error) {
      toast.error("Failed to save contact information");
    } else {
      toast.success("Contact information saved");
      await refreshOutlets();
    }
    setSavingSection(null);
  };

  const handleSaveSocial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOutlet) return;
    setSavingSection("social");

    const { error } = await supabase
      .from("outlets")
      .update({
        instagram_url: formData.instagram_url,
        facebook_url: formData.facebook_url,
        twitter_url: formData.twitter_url,
      })
      .eq("outlet_id", activeOutlet.outlet_id);

    if (error) {
      toast.error("Failed to save social profiles");
    } else {
      toast.success("Social profiles saved");
      await refreshOutlets();
    }
    setSavingSection(null);
  };

  const handleSocialHandle = (field: keyof typeof SOCIAL_CONFIGS) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const handle = sanitizeHandle(e.target.value);
    const config = SOCIAL_CONFIGS[field];
    const fullUrl = handle ? `${config.baseUrl}${handle}` : "";
    setFormData({ ...formData, [field]: fullUrl });
  };

  const loading = outletLoading;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Storefront settings */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Storefront settings</p>
          <p className="text-sm text-muted-foreground mt-1">Manage your storefront's custom URL. This information appears on your storefront profile section.</p>
        </div>
        <form onSubmit={handleSaveStorefront} className="space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="storefront-slug">Storefront URL</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">usedibs.com/</span>
                <Input
                  id="storefront-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="studio-bloom"
                  className="pl-[6.25rem]"
                  aria-invalid={slugStatus === 'taken'}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const protocol = window.location.protocol;
                  const host = window.location.hostname.replace(/^admin\./, "");
                  const port = window.location.port ? `:${window.location.port}` : "";
                  const url = `${protocol}//${host}${port}/${formData.slug}`;
                  window.open(url, "_blank");
                }}
                className="shrink-0 h-10 w-10 rounded-md border flex items-center justify-center hover:bg-muted transition-colors"
                title="View storefront"
              >
                <Eye className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {slugStatus === 'checking' && (
              <div className="flex items-center gap-1.5 animate-pulse">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium italic">Checking availability...</span>
              </div>
            )}
            {slugStatus === 'available' && (
              <div className="flex items-center gap-1.5">
                <div className="bg-green-500/10 p-0.5 rounded-full">
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <span className="text-xs text-green-600 font-medium">This storefront URL is available!</span>
              </div>
            )}
            {slugStatus === 'taken' && (
              <p className="text-xs text-destructive">This URL is already taken. Please choose a different one.</p>
            )}

            <p className="text-xs text-muted-foreground">Share this link with your customers: usedibs.com/{formData.slug || "your-slug"}</p>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingSection === 'storefront' || slugStatus === 'taken'}>
              {savingSection === 'storefront' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </div>

      {/* Business profile */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Business profile</p>
          <p className="text-sm text-muted-foreground mt-1">Update your business image and basic information.</p>
        </div>
        <form onSubmit={handleSaveProfile} className="space-y-6">
          <div className="space-y-3">
            <div className="border rounded-xl px-4 py-4 space-y-3">
              <div className="text-[10px] font-semibold text-muted-foreground leading-none">Profile picture</div>
              <div className="flex items-center gap-4">
                <div
                  className="relative w-16 h-16 rounded-full border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30 shrink-0 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : formData.avatar_url ? (
                    <img src={formData.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Upload image
                    </Button>
                    {formData.avatar_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveImage}
                        className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Supports: PNG, JPEG, JPG. Max 5MB. Recommended: 64x64px.</p>
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <div className="space-y-1.5">
              <Label htmlFor="business-name">Business name</Label>
              <Input
                id="business-name"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Studio Bloom"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="business-description">Description</Label>
              <Textarea
                id="business-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Yoga & Wellness Studio — Find your balance, nurture your soul."
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingSection === 'profile'}>
              {savingSection === 'profile' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </div>

      {/* Contact information */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Contact information</p>
          <p className="text-sm text-muted-foreground mt-1">How customers can reach you.</p>
        </div>
        <form onSubmit={handleSaveContact} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="business-address">Address</Label>
              <Input
                id="business-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Wellness Lane, Downtown"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="business-phone" className={cn(phoneError && "text-destructive")}>Phone</Label>
              <div
                className={cn(
                  "flex h-10 w-full items-center rounded-md border border-input bg-background px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                  phoneError && "border-destructive focus-within:ring-destructive"
                )}
              >
                <PhoneInput
                  id="business-phone"
                  international
                  defaultCountry="SG"
                  placeholder="8123 4567"
                  value={formData.phone_number || ""}
                  onChange={(val) => {
                    setFormData({ ...formData, phone_number: val || "" });
                    if (phoneError) {
                      if (!val || isValidPhoneNumber(val)) setPhoneError(null);
                    }
                  }}
                  onBlur={() => {
                    if (formData.phone_number && formData.phone_number.trim() !== "" && !isValidPhoneNumber(formData.phone_number)) {
                      setPhoneError("Enter a valid phone number");
                    }
                  }}
                  className="phone-input-onboarding"
                />
              </div>
              {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="business-email">Email</Label>
              <Input
                id="business-email"
                type="email"
                value={formData.business_email}
                onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                placeholder="hello@studiobloom.com"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingSection === 'contact'}>
              {savingSection === 'contact' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </div>

      {/* Social media */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Social media</p>
          <p className="text-sm text-muted-foreground mt-1">Connect your social profiles.</p>
        </div>
        <form onSubmit={handleSaveSocial} className="space-y-6">
          <div className="space-y-3">
            {(Object.entries(SOCIAL_CONFIGS) as [keyof typeof SOCIAL_CONFIGS, typeof SOCIAL_CONFIGS[keyof typeof SOCIAL_CONFIGS]][]).map(
              ([field, config]) => (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={field}>{config.label}</Label>
                  <div className="flex h-10 w-full items-center rounded-md border border-input bg-background pl-3 pr-1 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap select-none">
                      {config.display}
                    </span>
                    <input
                      id={field}
                      type="text"
                      placeholder={config.placeholder}
                      value={extractHandle(formData[field], config.baseUrl)}
                      onChange={handleSocialHandle(field)}
                      className="flex-1 min-w-0 text-sm font-medium bg-transparent outline-none placeholder:text-muted-foreground disabled:opacity-50 ml-0.5 py-2 md:text-sm"
                    />
                  </div>
                </div>
              )
            )}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingSection === 'social'}>
              {savingSection === 'social' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </div>

      {/* Programme refund policy (business-level) */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Programme refund policy</p>
          <p className="text-sm text-muted-foreground mt-1">
            When a customer cancels remaining sessions of a prepaid programme, how should they be refunded?
          </p>
        </div>
        <div className="space-y-3">
          {([
            { value: "pro_rata", title: "Pro-rata refund", desc: "Refund the value of the sessions they haven't attended yet." },
            { value: "credit_only", title: "Store credit only", desc: "No cash refund — record credit for the remaining sessions to use later." },
            { value: "no_refund", title: "No refund", desc: "Sessions are forfeited once the programme starts." },
          ] as const).map((opt) => {
            const selected = refundPolicy === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRefundPolicy(opt.value)}
                className={`w-full text-left border rounded-xl px-4 py-3 transition-colors ${selected ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-input hover:border-muted-foreground/40"}`}
              >
                <div className="text-sm font-medium">{opt.title}</div>
                <div className="text-xs text-muted-foreground leading-snug mt-0.5">{opt.desc}</div>
              </button>
            );
          })}
          <Button type="button" onClick={handleSaveRefundPolicy} disabled={savingRefund}>
            {savingRefund && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Upcoming bookings per customer (business-level) */}
      {bookingLimitLoaded && (
        <div className="p-6 border rounded-xl bg-card shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-base font-heading font-semibold text-foreground">Upcoming bookings per customer</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-prose">
                The most upcoming bookings one customer can hold when booking online or through your AI. The same
                email or phone number counts as one customer, and a weekly series counts once. You can still add
                bookings for them yourself.
              </p>
            </div>
            <Switch
              checked={bookingLimitOn}
              disabled={savingBookingLimit}
              onCheckedChange={setBookingLimitOn}
              aria-label="Limit upcoming bookings per customer"
            />
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            {bookingLimitOn && (
              <div className="space-y-1.5">
                <Label htmlFor="booking-limit">Maximum</Label>
                <Input
                  id="booking-limit"
                  type="number"
                  min={1}
                  max={50}
                  inputMode="numeric"
                  value={bookingLimit}
                  onChange={(event) => setBookingLimit(event.target.value)}
                  className="w-24"
                />
              </div>
            )}
            <Button type="button" onClick={handleSaveBookingLimit} disabled={savingBookingLimit}>
              {savingBookingLimit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </div>
      )}

      {/* Make-up auto-offer (business-level, Phase C3) */}
      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-base font-heading font-semibold text-foreground">Make-up auto-offer</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-prose">
              When a student is owed a make-up, automatically offer them an open seat in the same
              programme over the parent's channel (WhatsApp, Telegram or Instagram), with a
              time-limited claim link. If unclaimed, the offer expires and is retried later.
            </p>
          </div>
          <Switch
            checked={makeUpAutoOffer}
            disabled={savingMakeUp}
            onCheckedChange={handleToggleMakeUpAutoOffer}
            aria-label="Toggle make-up auto-offer"
          />
        </div>
      </div>
    </div>
  );
};

export default StorefrontBusinessDetails;
